/**
 * Diff视图组件
 *
 * 显示章节编辑历史版本与当前版本的差异对比
 * 使用 Monaco Editor Diff 组件展示左右对比视图
 * 左侧显示选择的历史版本，右侧显示当前最新版本
 */

import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import Editor from '@monaco-editor/react';
import type { ChapterHistory } from '../../types/chapter';
import { ChapterService } from '../../services/chapter';
import { getUserFriendlyErrorMessage } from '../../utils/errorMessages';
import { Icons } from './EditorIcons';

// ==================== Props接口 ====================

interface DiffViewerProps {
  /** 工作区ID */
  workspaceId: number;
  /** 章节ID */
  chapterId: number;
  /** 当前章节内容（用于右侧显示） */
  currentContent: string;
  /** 关闭回调 */
  onClose: () => void;
}

// ==================== Monaco Editor配置 ====================

const EDITOR_OPTIONS = {
  readOnly: true,
  minimap: { enabled: false },
  scrollBeyondLastLine: false,
  wordWrap: 'on' as const,
  lineNumbers: 'on' as const,
  renderLineHighlight: 'all' as const,
  renderWhitespace: 'selection' as const,
};

// ==================== 加载状态组件 ====================

const LoadingState: React.FC<{ onClose: () => void }> = memo(({ onClose }) => (
  <div className="flex-1 bg-white flex flex-col">
    <div className="p-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
      <h3 className="font-medium text-gray-900">编辑历史</h3>
      <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700 transition-colors">
        <Icons.close />
      </button>
    </div>
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        {Icons.loadingLarge}
        <p className="text-sm text-gray-600 mt-3">加载中...</p>
      </div>
    </div>
  </div>
));

LoadingState.displayName = 'LoadingState';

// ==================== 错误状态组件 ====================

interface ErrorStateProps {
  error: string;
  onRetry: () => void;
  onClose: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = memo(({ error, onRetry, onClose }) => (
  <div className="flex-1 bg-white flex flex-col">
    <div className="p-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
      <h3 className="font-medium text-gray-900">编辑历史</h3>
      <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700 transition-colors">
        <Icons.close />
      </button>
    </div>
    <div className="flex-1 flex items-center justify-center p-4">
      <div className="text-center">
        <Icons.error />
        <p className="text-sm text-red-600 mt-3 mb-4">{error}</p>
        <button onClick={onRetry} className="px-4 py-2 bg-ink-800 hover:bg-ink-700 text-white rounded shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-2">
          <Icons.refresh />
          <span>重试</span>
        </button>
      </div>
    </div>
  </div>
));

ErrorState.displayName = 'ErrorState';

// ==================== 空状态组件 ====================

const EmptyState: React.FC<{ onClose: () => void }> = memo(({ onClose }) => (
  <div className="flex-1 bg-white flex flex-col">
    <div className="p-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
      <h3 className="font-medium text-gray-900">编辑历史</h3>
      <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700 transition-colors">
        <Icons.close />
      </button>
    </div>
    <div className="flex-1 flex items-center justify-center p-4">
      <div className="text-center">
        <Icons.empty />
        <p className="text-sm text-gray-600 mt-3">暂无编辑历史</p>
        <p className="text-xs text-gray-500 mt-2">编辑章节后即可查看历史记录</p>
      </div>
    </div>
  </div>
));

EmptyState.displayName = 'EmptyState';

// ==================== 主组件 ====================

export const DiffViewer: React.FC<DiffViewerProps> = ({
  workspaceId,
  chapterId,
  currentContent,
  onClose
}) => {
  // ==================== 状态管理 ====================

  const [history, setHistory] = useState<ChapterHistory | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [selectedVersionContent, setSelectedVersionContent] = useState<string>('');
  const [selectedVersionEditor, setSelectedVersionEditor] = useState<{ type: string; name: string | null } | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [diffLoading, setDiffLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // ==================== 服务实例 ====================

  const chapterService = useMemo(() => new ChapterService(undefined), []);

  // ==================== 加载编辑历史 ====================

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await chapterService.getHistory(workspaceId, chapterId, 1, 100);
      setHistory(result);

      // 默认选择最新历史版本（第一个）
      if (result.versions.length > 0) {
        setSelectedVersion(result.versions[0].version);
        setSelectedVersionContent(result.versions[0].content);
      }
    } catch (err) {
      console.error('[DiffViewer] 加载编辑历史失败:', err);
      setError(getUserFriendlyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [workspaceId, chapterId, chapterService]);

  // ==================== 加载版本内容 ====================

  const loadVersionContent = useCallback(async (version: number) => {
    if (!history) return;

    try {
      setDiffLoading(true);
      setError(null);

      const versionData = history.versions.find(v => v.version === version);

      if (!versionData) {
        throw new Error('版本数据不完整');
      }

      setSelectedVersionContent(versionData.content);
      setSelectedVersionEditor({
        type: versionData.editor_type,
        name: versionData.editor_name,
      });
    } catch (err) {
      console.error('[DiffViewer] 加载版本内容失败:', err);
      setError(getUserFriendlyErrorMessage(err));
    } finally {
      setDiffLoading(false);
    }
  }, [history]);

  // ==================== 重试加载 ====================

  const handleRetry = useCallback(() => {
    if (error && !history) {
      loadHistory();
    } else {
      if (selectedVersion !== null) {
        loadVersionContent(selectedVersion);
      }
    }
  }, [error, history, selectedVersion, loadHistory, loadVersionContent]);

  // ==================== 效果钩子 ====================

  // 组件挂载时加载历史
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // 版本变化时加载内容
  useEffect(() => {
    if (selectedVersion !== null) {
      loadVersionContent(selectedVersion);
    }
  }, [selectedVersion, loadVersionContent]);

  // ==================== 渲染函数 ====================

  // 加载中状态
  if (loading) {
    return <LoadingState onClose={onClose} />;
  }

  // 错误状态
  if (error) {
    return <ErrorState error={error} onRetry={handleRetry} onClose={onClose} />;
  }

  // 空状态 - 没有历史版本
  if (!history || history.versions.length === 0) {
    return <EmptyState onClose={onClose} />;
  }

  // 正常状态 - 显示Diff视图
  return (
    <div className="flex-1 bg-white flex flex-col">
      {/* 头部 */}
      <div className="p-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
        <h3 className="font-medium text-gray-900">编辑历史</h3>
        <button onClick={onClose} className="p-1 text-gray-500 hover:text-gray-700 transition-colors">
          <Icons.close />
        </button>
      </div>

      {/* 版本选择器 - 简化为单个选择器 */}
      <div className="p-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700 font-medium">历史版本:</label>
          <select
            value={selectedVersion ?? ''}
            onChange={(e) => setSelectedVersion(e.target.value ? parseInt(e.target.value) : null)}
            disabled={diffLoading}
            className="flex-1 text-sm border border-gray-200 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
          >
            <option value="">-- 请选择要对比的历史版本 --</option>
            {history.versions.map((version) => (
              <option key={version.id} value={version.version}>
                版本 {version.version} - {new Date(version.created_at).toLocaleString('zh-CN')} ({version.word_count} 字)
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Diff编辑器 - 左侧历史版本，右侧当前版本 */}
      <div className="flex-1 overflow-hidden">
        {!selectedVersion ? (
          <div className="h-full flex items-center justify-center p-4">
            <div className="text-center">
              <Icons.empty />
              <p className="text-sm text-gray-600 mt-3">请选择要对比的历史版本</p>
              <p className="text-xs text-gray-500 mt-2">右侧将显示当前最新版本</p>
            </div>
          </div>
        ) : diffLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              {Icons.loadingLarge}
              <p className="text-sm text-gray-600 mt-3">加载对比中...</p>
            </div>
          </div>
        ) : (
          <div className="h-full flex flex-col">
            {/* 并排编辑器 */}
            <div className="flex-1 flex gap-1">
              {/* 左侧：历史版本 */}
              <div className="flex-1 flex flex-col border-r border-gray-200">
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    历史版本 v{selectedVersion}
                  </span>
                  {selectedVersionEditor && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      selectedVersionEditor.type === 'agent' 
                        ? 'bg-paper-100 text-paper-800' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {selectedVersionEditor.name || (selectedVersionEditor.type === 'agent' ? '智能体' : '用户')}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <Editor
                    height="100%"
                    language="markdown"
                    value={selectedVersionContent}
                    options={EDITOR_OPTIONS}
                    loading={
                      <div className="h-full flex items-center justify-center">
                        <div className="text-center">
                          {Icons.loadingLarge}
                          <p className="text-sm text-gray-600 mt-3">加载编辑器中...</p>
                        </div>
                      </div>
                    }
                  />
                </div>
              </div>
              {/* 右侧：当前版本 */}
              <div className="flex-1 flex flex-col">
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-sm font-medium text-paper-700">
                  当前版本
                </div>
                <div className="flex-1">
                  <Editor
                    height="100%"
                    language="markdown"
                    value={currentContent}
                    options={EDITOR_OPTIONS}
                    loading={
                      <div className="h-full flex items-center justify-center">
                        <div className="text-center">
                          {Icons.loadingLarge}
                          <p className="text-sm text-gray-600 mt-3">加载编辑器中...</p>
                        </div>
                      </div>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiffViewer;
